/**
 * Parser del Markdown minimo che il modello e' autorizzato a scrivere.
 *
 * Le istruzioni chiedono al modello paragrafi, grassetto, corsivo, elenchi e
 * una riga finale `Riferimento: …`. Questo parser riconosce esattamente quello
 * e produce blocchi che il componente trasforma in nodi React: nessuna stringa
 * del modello finisce mai in `innerHTML`, e l'HTML eventuale resta testo.
 *
 * Il parser lavora anche sul testo parziale che arriva in streaming: un
 * asterisco non ancora chiuso resta testo finche' non si chiude.
 */

export type Inline =
  | { tipo: "testo" | "forte" | "corsivo"; testo: string }
  | { tipo: "collegamento"; testo: string; idNodo: string }
  | { tipo: "acapo" };

export type Blocco =
  | { tipo: "paragrafo"; inline: Inline[] }
  | { tipo: "elenco"; voci: Inline[][] }
  | { tipo: "riferimento"; inline: Inline[] };

const PREFISSO_RIFERIMENTO = /^Riferimento:\s*(.+)$/;
const VOCE_ELENCO = /^\s*[-*•]\s+(.*)$/;

export function analizzaMarkdown(testo: string): Blocco[] {
  const blocchi: Blocco[] = [];
  // Le righe di un paragrafo: due spazi a fine riga sono l'a capo «duro» del
  // Markdown (il modello lo usa per i campi di un modello da compilare); una
  // riga sola e' solo testo che continua.
  let paragrafo: { testo: string; acapo: boolean }[] = [];
  let elenco: Inline[][] | null = null;

  const chiudiParagrafo = (): void => {
    if (paragrafo.length) {
      const inline: Inline[] = [];
      let tratto = "";
      const svuota = (): void => {
        if (tratto) inline.push(...analizzaInline(tratto));
        tratto = "";
      };
      for (const [i, riga] of paragrafo.entries()) {
        tratto = tratto ? `${tratto} ${riga.testo}` : riga.testo;
        if (riga.acapo && i < paragrafo.length - 1) {
          svuota();
          inline.push({ tipo: "acapo" });
        }
      }
      svuota();
      blocchi.push({ tipo: "paragrafo", inline });
      paragrafo = [];
    }
  };
  const chiudiElenco = (): void => {
    if (elenco) {
      blocchi.push({ tipo: "elenco", voci: elenco });
      elenco = null;
    }
  };

  for (const rigaGrezza of testo.replace(/\r/g, "").split("\n")) {
    const riga = rigaGrezza.trimEnd();
    if (riga.trim() === "") {
      chiudiParagrafo();
      chiudiElenco();
      continue;
    }
    const voce = VOCE_ELENCO.exec(riga);
    if (voce) {
      chiudiParagrafo();
      (elenco ??= []).push(analizzaInline(voce[1]));
      continue;
    }
    const riferimento = PREFISSO_RIFERIMENTO.exec(riga.trim());
    if (riferimento) {
      chiudiParagrafo();
      chiudiElenco();
      blocchi.push({ tipo: "riferimento", inline: analizzaInline(riferimento[1].trim()) });
      continue;
    }
    chiudiElenco();
    paragrafo.push({ testo: riga.trim(), acapo: / {2,}$/.test(rigaGrezza.replace(/\r/g, "")) });
  }
  chiudiParagrafo();
  chiudiElenco();
  return blocchi;
}

/** Solo gli id dell'alberatura: lettere, cifre, trattini. Tutto il resto non e' un nodo. */
const ID_NODO = /^nodo:([A-Za-z0-9_-]+)$/;

/**
 * Grassetto `**x**`, corsivo `*x*` e collegamenti `[x](nodo:id)`; marcatori non
 * chiusi restano testo. Un link verso qualunque altra cosa (http, javascript,
 * mailto…) perde la destinazione e resta solo il suo testo: il modello non ha
 * motivo di mandare l'operatore fuori dal back-office.
 */
export function analizzaInline(testo: string): Inline[] {
  const risultato: Inline[] = [];
  let resto = testo;
  const aggiungiTesto = (t: string): void => {
    if (!t) return;
    const ultimo = risultato[risultato.length - 1];
    if (ultimo && ultimo.tipo === "testo") ultimo.testo += t;
    else risultato.push({ tipo: "testo", testo: t });
  };

  while (resto.length) {
    const forte = /\*\*(?=\S)([^*]+?)(?<=\S)\*\*/.exec(resto);
    const corsivo = /(?<!\*)\*(?=\S)([^*]+?)(?<=\S)\*(?!\*)/.exec(resto);
    const link = /\[([^\]]+)\]\(([^)\s]*)\)/.exec(resto);
    const candidati = [
      forte && { m: forte, tipo: "forte" as const },
      corsivo && { m: corsivo, tipo: "corsivo" as const },
      link && { m: link, tipo: "link" as const },
    ]
      .filter((c): c is { m: RegExpExecArray; tipo: "forte" | "corsivo" | "link" } => c !== null)
      .sort((a, b) => a.m.index - b.m.index);
    const primo = candidati[0];
    if (!primo) {
      aggiungiTesto(resto);
      break;
    }
    aggiungiTesto(resto.slice(0, primo.m.index));
    if (primo.tipo === "link") {
      const id = ID_NODO.exec(primo.m[2]);
      if (id) risultato.push({ tipo: "collegamento", testo: primo.m[1], idNodo: id[1] });
      else aggiungiTesto(primo.m[1]);
    } else {
      // Dentro il grassetto puo' esserci un collegamento: il link vince, il
      // resto del contenuto tiene l'enfasi.
      for (const parte of analizzaInline(primo.m[1])) {
        if (parte.tipo === "collegamento" || parte.tipo === "acapo") risultato.push(parte);
        else risultato.push({ tipo: primo.tipo, testo: parte.testo });
      }
    }
    resto = resto.slice(primo.m.index + primo.m[0].length);
  }
  return risultato;
}

/**
 * Quanti caratteri rivelare al prossimo fotogramma, dato quanto testo e'
 * arrivato ma non e' ancora mostrato: almeno due, di piu' se il modello corre.
 * Cosi' la risposta scorre con ritmo costante invece di comparire a blocchi.
 */
export function prossimoPasso(arretrato: number): number {
  if (arretrato <= 0) return 0;
  return Math.min(arretrato, Math.max(2, Math.floor(arretrato / 20)));
}
